import { useSelector } from '@/hooks/useSelector';
import { useMemo } from 'react';
import { DeliveryItems } from '@/api';
import { ReceivingStatus } from '@/pages/Receiving/PageReceiving/types';
import { isMultipleDeliveryExcluded } from '@/utils/user';

export const useDeliverySelectionRules = (
  selectedDeliveries: DeliveryItems[]
) => {
  const { PARTIAL, NEW, IN_USE } = ReceivingStatus;
  const loggedUser = useSelector(state => state.user);
  const multipleDeliveryExcluded = isMultipleDeliveryExcluded(loggedUser);

  const selectedCodes = useMemo(
    () => new Set(selectedDeliveries.map(d => d.codeDelivery)),
    [selectedDeliveries]
  );

  const selectedStatus = useMemo(
    () => ({
      hasPartial: selectedDeliveries.some(d => d.status === PARTIAL),
      hasNew: selectedDeliveries.some(d => d.status === NEW),
    }),
    [selectedDeliveries, PARTIAL, NEW]
  );

  const isChecked = (codeDelivery: string): boolean =>
    selectedCodes.has(codeDelivery);

  const isDisabled = (delivery: DeliveryItems): boolean => {
    if (delivery.status === IN_USE) return true;

    if (selectedStatus.hasPartial) {
      return !selectedCodes.has(delivery.codeDelivery);
    }

    if (
      multipleDeliveryExcluded &&
      selectedStatus.hasNew &&
      delivery.status === NEW
    ) {
      return !selectedCodes.has(delivery.codeDelivery);
    }

    if (selectedStatus.hasNew) {
      return delivery.status === PARTIAL;
    }

    return false;
  };

  return {
    isChecked,
    isDisabled,
    selectedCodes,
  };
};
